/*
 * Copyright (c) 2024. Bluemate B.V.
 */
import './style/customerDetails.scss';

import {
    AwesomeIcon,
    ChangeFunction,
    ChangeKeyFunction,
    CloseButton,
    Loader,
    preventDefault,
    WithBackground
} from '@dvrd/dvr-controls';

import React, {MouseEventHandler, useContext, useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import {StripeAddress, StripeCustomer, StripeTaxID, StripeVerificationStatus} from '../../../../util/interfaces';

import {BluemateButton, BluemateInput, BluemateSelect} from '../../../controls';

import {AppContext} from '../../../misc/appContext';

import {COUNTRIES} from '../../../../util/countryUtil';

import {IconName} from '@fortawesome/fontawesome-svg-core';

import classNames from 'classnames';

import InfoBanner from '../../../misc/infoBanner';

interface Props {
    onClose: MouseEventHandler;
    onChangeCustomer: ChangeKeyFunction<keyof StripeCustomer, string>;
    onChangeCustomerAddress: ChangeKeyFunction<keyof StripeAddress, string>;
    onChangeTaxID: ChangeFunction<string>;
    onAddTaxID: MouseEventHandler;
    onDeleteTaxID: (taxID: StripeTaxID) => MouseEventHandler;
    onSubmitCustomer: MouseEventHandler;
    customer: StripeCustomer;
    taxIDs: Array<StripeTaxID>;
    loading: boolean;
    taxIDValue: string;
    active: boolean;
    hasChanges: boolean;
}

export default function CustomerDetails(props: Props) {
    const {t} = useTranslation();
    const {account} = useContext(AppContext).accountContext;
    const {
        onClose,
        onChangeCustomer,
        onChangeCustomerAddress,
        onChangeTaxID,
        onAddTaxID,
        onDeleteTaxID,
        onSubmitCustomer,
        customer,
        taxIDs,
        loading,
        taxIDValue,
        active,
        hasChanges
    } = props;
    const countries = useMemo(() => {
        return Object.entries(COUNTRIES).map(([countryCode, countryLabel]) => ({
            value: countryCode,
            label: countryLabel
        }));
    }, []);

    function renderBody() {
        const {address} = customer;
        return (
            <div className='body-container'>
                <Loader active={loading} />
                <InfoBanner className='disclaimer' persistDismiss={false}>
                    {t(
                        'settings.subscription.customerDetailsDisclaimer',
                        'Let op, deze gegevens worden enkel gebruikt voor de facturen die je van ons ontvangt. Deze gegevens worden niet gebruikt op de facturen die we naar jouw klanten sturen.'
                    )}
                </InfoBanner>
                <form className='customer-container' onSubmit={preventDefault}>
                    <BluemateInput
                        onChange={onChangeCustomer('name')}
                        value={customer.name ?? ''}
                        label={t('settings.subscription.companyName', 'Bedrijfsnaam')}
                        placeholder={account?.companyName ?? t('settings.subscription.companyName', 'Bedrijfsnaam')}
                    />
                    <BluemateInput
                        onChange={onChangeCustomer('email')}
                        value={customer.email ?? ''}
                        label={t('settings.subscription.invoiceEmail', 'E-mail voor facturatie')}
                        placeholder={account?.email ?? t('settings.subscription.email', 'E-mailadres')}
                    />
                    <div className='row street-row'>
                        <BluemateInput
                            onChange={onChangeCustomerAddress('line1')}
                            value={address.line1 ?? ''}
                            label={t('settings.subscription.streetNumber', 'Straat + nummer')}
                            placeholder={account?.address ?? t('settings.subscription.address', 'Adres')}
                        />
                        <BluemateInput
                            onChange={onChangeCustomerAddress('line2')}
                            value={address.line2 ?? ''}
                            label={t('settings.subscription.addition', 'Toevoeging')}
                            placeholder={t('settings.subscription.addition', 'Toevoeging')}
                        />
                    </div>
                    <div className='row'>
                        <BluemateInput
                            onChange={onChangeCustomerAddress('postal_code')}
                            value={address.postal_code ?? ''}
                            label={t('settings.subscription.zipCode', 'Postcode')}
                            placeholder={account?.zipCode ?? t('settings.subscription.zipCode', 'Postcode')}
                        />
                        <BluemateInput
                            onChange={onChangeCustomerAddress('city')}
                            value={address.city ?? ''}
                            label={t('settings.subscription.city', 'Stad')}
                            placeholder={account?.city ?? t('settings.subscription.city', 'Stad')}
                        />
                    </div>
                    <BluemateSelect
                        value={customer.address.country ?? 'NL'}
                        onChange={onChangeCustomerAddress('country')}
                        items={countries}
                        label={t('settings.subscription.country', 'Land')}
                        itemsHeight='20rem'
                        itemsPosition='top'
                    />
                </form>
                {renderTaxIDs()}
            </div>
        );
    }

    function renderTaxIDs() {
        return (
            <section className='tax-ids-container'>
                <label className='section-title'>{t('settings.subscription.vatNumbers', 'BTW-nummers')}</label>
                <div className='tax-ids'>{taxIDs.map(renderTaxID)}</div>
                <div className='add-row'>
                    <BluemateInput
                        onChange={onChangeTaxID}
                        value={taxIDValue}
                        label={t('settings.subscription.addNewVat', 'Nieuw BTW-nummer toevoegen')}
                        placeholder={t('settings.subscription.vatNumber', 'BTW-nummer')}
                    />
                    <BluemateButton
                        onClick={onAddTaxID}
                        label={t('settings.subscription.addBtn', '+ Toevoegen')}
                        secondary
                    />
                </div>
            </section>
        );
    }

    function renderTaxID(taxId: StripeTaxID, index: number) {
        let verificationIcon: IconName = 'clock';
        if (taxId.verification?.status === StripeVerificationStatus.VERIFIED) verificationIcon = 'circle-check';
        else if (taxId.verification?.status !== StripeVerificationStatus.PENDING) verificationIcon = 'circle-xmark';
        return (
            <div className='tax-id' key={index}>
                {!!taxId.verification && (
                    <div className='verification-info'>
                        <AwesomeIcon
                            name={verificationIcon}
                            prefix='far'
                            className={classNames('verification-icon', taxId.verification?.status)}
                        />
                        <p className='verification-message'>
                            {taxId.verification.status === StripeVerificationStatus.VERIFIED && (
                                <>
                                    {t('settings.subscription.vatVerified', 'BTW-nummer gevalideerd')}
                                    <br />
                                    <br />
                                    {taxId.verification.verified_name}
                                    <br />
                                    {taxId.verification.verified_address}
                                </>
                            )}
                            {taxId.verification.status === StripeVerificationStatus.PENDING && (
                                <>{t('settings.subscription.vatPending', 'BTW-nummer wordt gevalideerd')}</>
                            )}
                            {taxId.verification.status === StripeVerificationStatus.UNVERIFIED && (
                                <>{t('settings.subscription.vatInvalid', 'Het BTW-nummer is niet geldig')}</>
                            )}
                            {taxId.verification.status === StripeVerificationStatus.UNAVAILABLE && (
                                <>
                                    {t(
                                        'settings.subscription.vatUnavailable',
                                        'Het BTW-nummer kan niet gevalideerd worden'
                                    )}
                                </>
                            )}
                        </p>
                    </div>
                )}
                <label className='tax-id-label'>{taxId.value}</label>
                <AwesomeIcon name='trash-alt' className='btn-delete' onClick={onDeleteTaxID(taxId)} />
            </div>
        );
    }

    return (
        <WithBackground active={active} onClose={onClose}>
            <div className='default-popup customer-details'>
                <div className='header-container'>
                    <label className='header-title'>
                        {t('settings.subscription.invoiceDetailsTitle', 'Factuurgegevens')}
                    </label>
                    <CloseButton onClick={onClose} />
                </div>
                {renderBody()}
                <div className='footer-container btn-2'>
                    <BluemateButton
                        onClick={onClose}
                        label={t('settings.subscription.close', 'Sluiten')}
                        disabled={loading}
                        secondary
                    />
                    <BluemateButton
                        onClick={onSubmitCustomer}
                        label={t('settings.subscription.save', 'Opslaan')}
                        disabled={loading || !hasChanges}
                    />
                </div>
            </div>
        </WithBackground>
    );
}
